import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as braintree from 'braintree-web';
import { CartTotalsComponent } from '../../ui/cart-totals/cart-totals.component';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { IBtCreditCard } from '../../interfaces/braintree.interfaces';
import { BtCreditCardSelectorComponent } from '../../ui/bt-credit-card-selector/bt-credit-card-selector.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { ErrorDialogComponent } from '../../ui/error-dialog/error-dialog.component';
import { CartStateService } from '../../data-access/cart-state.service';
import { PaymentsService } from '../../data-access/payments.service';
import { BraintreeService } from '../../data-access/braintree.service';
import { OrderSummaryComponent } from '../../ui/order-summary/order-summary.component';
import { UPDATE_USER } from '@equine-ui-nx/injection-tokens';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';

@Component({
  standalone: true,
  selector: 'equine-ui-nx-pay-with-bt-credit-card',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    CommonModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatRadioModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    CartTotalsComponent,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    BtCreditCardSelectorComponent,
    OrderSummaryComponent
  ],
  providers: [
    PaymentsService,
    MatSnackBar
  ],
  templateUrl: './pay-with-bt-credit-card.component.html',
  styleUrl: './pay-with-bt-credit-card.component.scss',
})
export class PayWithBtCreditCardComponent implements OnInit, OnDestroy {
  private braintreeService = inject(BraintreeService);
  private paymentsService = inject(PaymentsService);
  private cartId: string | undefined;
  
  private router = inject(Router);
  private cartState = inject(CartStateService);

  cartReference = this.paymentsService.getLoggedInUserId() || '';

  orders = this.cartState.ordersForSum;
  subTotal = this.cartState.subtotal;
  totalItems = this.cartState.totalItems;
  donationOrder = this.cartState.donationOrder;
  donationOrderTotal = this.cartState.donationOrderTotal;
  total = this.cartState.total;
  coupon = this.cartState.coupon;

  private _snackBar = inject(MatSnackBar);
  private _dialog = inject(MatDialog);

  private clientInstance!: braintree.Client;
  private dataCollectorInstance!: braintree.DataCollector;
  hostedFieldsInstance!: braintree.HostedFields;

  loading = signal(true);
  cartCompleted = signal(false);
  generalError = signal<string | null>(null);

  cardholdersName!: string;
  disablePayButton = signal(true);
  vaultCard = false;

  paying = signal(false);
  cartError = signal(null);

  paymentTypeSelected: string | null = null;

  creditCards: IBtCreditCard[] = [];
  creditCardToken: string | null = null;

  optionSelected!: 'savedCard' | 'manualCard';

  constructor(
    private cdRef: ChangeDetectorRef,
    @Inject(UPDATE_USER) private updateUserToken: BehaviorSubject<void>
  ) {}

  ngOnInit() {
    this.getAmount();
  }

  getAmount() {
    this.loading.set(true);
    
    this.paymentsService.getCartSummary(undefined, this.cartReference)
      .subscribe({
        next: (response) => {
          this.loading.set(false);
          
          if (response.status === 'COMPLETE') {
            this.cartCompleted.set(true);
          } else {
            this.initBraintree();
          }
        },
        error: (error) => {
          const errorMessage = error?.error?.message || 'An error ocurred. Cannot load cart'
          this.cartError.set(errorMessage);
          this.loading.set(false);
        }
      });
  }

  initBraintree() {
    this.braintreeService.getBraintreeTokenAndCustomerData(
      this.cartReference
    ).subscribe({
      next: (response: {clientToken: string, btCustomer: {creditCards?: IBtCreditCard[]}}) => {
        this.getBraintreeInstance(response.clientToken);
        this.creditCards = response.btCustomer.creditCards || [];
      },
      error: () => {
        void 0;
      }
    });
  }

  getBraintreeInstance(clientToken: string) {
    braintree.client.create({authorization: clientToken})
      .then((clientInstance: braintree.Client) => {

        this.clientInstance = clientInstance;

        this.setDataCollector();
        this.createBraintreeCreditCardHostedFields();
      });
  }

  setDataCollector() {
    braintree.dataCollector.create({
      client: this.clientInstance
    }).then((dataCollectorInstance: braintree.DataCollector) => {
      this.dataCollectorInstance = dataCollectorInstance;
    })
    .catch(() => {
      void 0;
    }); 
  }

  createBraintreeCreditCardHostedFields() {
    braintree.hostedFields.create({
      client: this.clientInstance,
      styles: {
        'input': {
          'font-size': '16px',
          'font-family': 'roboto, verdana, sans-serif',
          'font-weight': 'lighter',
          'color': '#1A1C1C'
        },
        ':focus': {
          'color': '#1A1C1C'
        },
        '.valid': {
          'color': '#1A1C1C'
        },
        '.invalid': {
          'color': '#AB2D25'
        }
      },
      fields: {
        number: {
          selector: '#card-number',
          placeholder: '1111 1111 1111 1111'
        },
        cvv: {
          selector: '#cvv',
          placeholder: '111'
        },
        expirationDate: {
          selector: '#expiration-date',
          placeholder: 'MM/YY'
        },
        cardholderName: {
          selector: '#card-holder-name',
          placeholder: 'e.g. JOHN DOE'
        },
        postalCode: {
          selector: '#zip-code',
          placeholder: '11111'
        }
      }
    }).then((hostedFieldsInstance: braintree.HostedFields) => {
      
      this.hostedFieldsInstance = hostedFieldsInstance;

      hostedFieldsInstance.on('focus', (event) => {
        const field = event.fields[event.emittedBy];
        const label = this.findLabel(field);
        label?.classList.remove('filled'); // added and removed css classes
      });

      hostedFieldsInstance.on('blur', (event) => {
        const field = event.fields[event.emittedBy];
        const label = this.findLabel(field); // fetched label to apply custom validations
        this.disablePayButton.set(!!Object.values(event.fields).find(each => each.isValid === false));
        this.cdRef.detectChanges();
      });

      hostedFieldsInstance.on('empty', (event) => {
        const field = event.fields[event.emittedBy];
        // can add custom code for custom validations here
      });

      hostedFieldsInstance.on('validityChange', (event) => {
        console.log(event.emittedBy)
        const field = event.fields[event.emittedBy];
        const label = this.findLabel(field);
        if (field.isValid) { // applying custom css and validations
          label?.classList.remove('invalid');
        } else {
          label?.classList.add('invalid');
        }

        this.disablePayButton.set(!!Object.values(event.fields).find(each => each.isValid === false));
        this.cdRef.detectChanges();
        // can add custom code for custom validations here
      });
    });
  }

  tokenizeUserDetails() {
    this.paying.set(true);
    this.cdRef.detectChanges();

    this.hostedFieldsInstance.tokenize({cardholderName: this.cardholdersName}).then((payload) => {
      this.braintreeService.payWithCreditCardNonce(
        this.cartState.cartId() as string,
        payload.nonce,
        this.dataCollectorInstance.deviceData,
        this.vaultCard
      ).subscribe({
        next: (success) => {
          this.paymentsService.setCartAsEmptyAfterPayment();

          this._snackBar.open(`Success!`, 'Dismiss', {
            duration: 5000,
            panelClass: ['success-snackbar'],
          });
          this.router.navigate(['payments', 'success']);
        },
        error: (error: any) => {
          this.paying.set(false);
          this.cdRef.detectChanges();

          this._dialog.open(ErrorDialogComponent, {
            data: {
              message: error?.error?.message || error?.message || `An error occured. Please try again`
            }
          });
        }
      });
    }).catch((error: any) => {
      this._dialog.open(ErrorDialogComponent, {
        data: {
          message: error?.error?.message || error?.message || `An error occured. Please try again`,
          message2: `The following fields are not valid: ${error.details.invalidFieldKeys.join(', ')}.`
        }
      });
    });
  }

  findLabel(field: any) {
    console.log(field.container.id)
    // return document.querySelector('.hosted-field--label[for="' + field.container.id + '"]');
    return document.querySelector(`#${field.container.id}-label`);
  }

  onRadioGroupChange(change: MatRadioChange) {
    if (change.value) {
      this.optionSelected = change.value;
    }
  }

  onTokenSelected(token: string | null) {
    this.creditCardToken = token;
  }

  payWithSavedCard() {
    if (this.optionSelected!=='savedCard' || !this.creditCardToken) {
      return;
    }

    this.paying.set(true);
    this.cdRef.detectChanges();

    this.braintreeService.payWithSavedCreditCard(
      this.cartState.cartId() as string,
      this.creditCardToken,
      this.dataCollectorInstance.deviceData
    ).subscribe({
      next: (success) => {
        this.paymentsService.setCartAsEmptyAfterPayment();
        this.fetchUserIfNeeded(success);

        this._snackBar.open(`Success!`, 'Dismiss', {
          duration: 5000,
          panelClass: ['success-snackbar'],
        });
        this.router.navigate(['payments', 'success']);
      },
      error: (error: any) => {
        this.paying.set(false);
        this.cdRef.detectChanges();

        this._dialog.open(ErrorDialogComponent, {
          data: {
            message: error?.error?.message || error?.message || `An error occured. Please try again`
          }
        });      }
    });

  }

  fetchUserIfNeeded(successResponse: any) {
    this.updateUserToken.next();
  }

  goBack() {
    this.router.navigate(['payments', 'payment-summary'])
  }

  ngOnDestroy(): void {
    void 0;
  }

}
